import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { SA_UTC_OFFSET_MINUTES } from "../../../../../utils/Constants";
import { TIMESHEET_SLOT_TABS } from "../constants";
import {
  getEventTrainingDateBounds,
  isCheckInWithinSlotMargin,
  isCheckOutWithinSlotMargin,
} from "./addSlotMarginHelpers";

dayjs.extend(utc);

export const EMPTY_ADD_SLOT_FORM = {
  checkInDate: "",
  checkInTime: "",
  checkOutDate: "",
  checkOutTime: "",
};

export const ADD_SLOT_FORM_FIELDS = [
  "checkInDate",
  "checkInTime",
  "checkOutDate",
  "checkOutTime",
];

export const combineDateAndTimeForApi = (date, time) => {
  if (!date?.trim() || !time?.trim()) {
    return "";
  }

  const combined = dayjs(`${date}T${time}`);
  if (!combined.isValid()) {
    return "";
  }

  return combined.utcOffset(SA_UTC_OFFSET_MINUTES, true).format();
};

export const buildAddSlotPayload = (values) => {
  const check_in_at = combineDateAndTimeForApi(
    values.checkInDate,
    values.checkInTime,
  );
  const check_out_at =
    values.checkOutDate?.trim() && values.checkOutTime?.trim()
      ? combineDateAndTimeForApi(values.checkOutDate, values.checkOutTime)
      : null;

  return { check_in_at, check_out_at };
};

export const buildEditSlotPayload = (slotId, values) => ({
  id: slotId,
  ...buildAddSlotPayload(values),
});

export const mapSlotRowToFormValues = (row = {}) => {
  const checkIn = row.check_in_at ? dayjs(row.check_in_at) : null;
  const checkOut = row.check_out_at ? dayjs(row.check_out_at) : null;

  return {
    checkInDate: checkIn?.isValid() ? checkIn.format("YYYY-MM-DD") : "",
    checkInTime: checkIn?.isValid() ? checkIn.format("HH:mm") : "",
    checkOutDate: checkOut?.isValid() ? checkOut.format("YYYY-MM-DD") : "",
    checkOutTime: checkOut?.isValid() ? checkOut.format("HH:mm") : "",
  };
};

const isDateOutsideEventBounds = (dateValue, minDate, maxDate) => {
  if (!dateValue?.trim()) {
    return false;
  }

  const date = dayjs(dateValue);
  if (!date.isValid()) {
    return true;
  }

  if (minDate?.isValid() && date.isBefore(minDate, "day")) {
    return true;
  }

  if (maxDate?.isValid() && date.isAfter(maxDate, "day")) {
    return true;
  }

  return false;
};

const isDateTimeInFuture = (dateValue, timeValue) => {
  if (!dateValue?.trim() || !timeValue?.trim()) {
    return false;
  }

  const dateTime = dayjs(`${dateValue}T${timeValue}`);
  return dateTime.isValid() && dateTime.isAfter(dayjs());
};

const isCheckOutDateBeforeCheckInDate = (checkOutDate, checkInDate) => {
  const checkOut = dayjs(checkOutDate);
  const checkIn = dayjs(checkInDate);

  if (!checkOut.isValid() || !checkIn.isValid()) {
    return false;
  }

  return checkOut.isBefore(checkIn, "day");
};

const isCheckOutTimeBeforeCheckInTimeOnSameDay = (
  values,
) => {
  const { checkInDate, checkInTime, checkOutDate, checkOutTime } = values;

  if (
    !checkInDate?.trim() ||
    !checkInTime?.trim() ||
    !checkOutDate?.trim() ||
    !checkOutTime?.trim()
  ) {
    return false;
  }

  const checkInDay = dayjs(checkInDate);
  const checkOutDay = dayjs(checkOutDate);

  if (
    !checkInDay.isValid() ||
    !checkOutDay.isValid() ||
    !checkOutDay.isSame(checkInDay, "day")
  ) {
    return false;
  }

  const checkInDateTime = dayjs(`${checkInDate}T${checkInTime}`);
  const checkOutDateTime = dayjs(`${checkOutDate}T${checkOutTime}`);

  if (!checkInDateTime.isValid() || !checkOutDateTime.isValid()) {
    return false;
  }

  return !checkOutDateTime.isAfter(checkInDateTime);
};

const validateCheckOutFields = (values, errors, minDate, maxDate) => {
  const hasCheckOutDate = Boolean(values.checkOutDate?.trim());
  const hasCheckOutTime = Boolean(values.checkOutTime?.trim());
  const hasCheckInDate = Boolean(values.checkInDate?.trim());

  if (!hasCheckOutDate && !hasCheckOutTime) {
    return;
  }

  if (hasCheckOutDate) {
    if (!dayjs(values.checkOutDate).isValid()) {
      errors.checkOutDate = "invalidDate";
    } else if (
      isDateOutsideEventBounds(values.checkOutDate, minDate, maxDate)
    ) {
      errors.checkOutDate = "slotDateOutsideEventPeriod";
    } else if (
      hasCheckInDate &&
      isCheckOutDateBeforeCheckInDate(values.checkOutDate, values.checkInDate)
    ) {
      errors.checkOutDate = "checkoutDateMustBeAfterCheckinDate";
    }
  }

  if (hasCheckOutTime && !dayjs(values.checkOutTime, "HH:mm").isValid()) {
    errors.checkOutTime = "invalidTime";
  }

  if (
    hasCheckOutDate &&
    hasCheckOutTime &&
    !errors.checkOutDate &&
    !errors.checkOutTime
  ) {
    if (isDateTimeInFuture(values.checkOutDate, values.checkOutTime)) {
      errors.checkOutTime = "slotDateTimeCannotBeFuture";
      return;
    }

    if (isCheckOutTimeBeforeCheckInTimeOnSameDay(values)) {
      errors.checkOutTime = "checkoutTimeMustBeAfterCheckinTime";
    }
  }
};

const validateEditCheckoutPreserved = (values, errors, originalSlot) => {
  if (!originalSlot?.check_out_at) {
    return;
  }

  if (!values.checkOutDate?.trim()) {
    errors.checkOutDate = "checkoutCannotBeCleared";
  }

  if (!values.checkOutTime?.trim()) {
    errors.checkOutTime = "checkoutCannotBeCleared";
  }
};

const validateSlotMarginFields = (values, errors, context = {}) => {
  const { slotType, applicant, event } = context;
  const hasCheckIn =
    values.checkInDate?.trim() &&
    values.checkInTime?.trim() &&
    !errors.checkInDate &&
    !errors.checkInTime;

  if (hasCheckIn && !isDateTimeInFuture(values.checkInDate, values.checkInTime)) {
    if (
      !isCheckInWithinSlotMargin({
        dateStr: values.checkInDate,
        timeStr: values.checkInTime,
        slotType,
        event,
        applicant,
      })
    ) {
      errors.checkInTime =
        slotType === TIMESHEET_SLOT_TABS.trainings
          ? "slotTrainingCheckInOutsideMargin"
          : "slotShiftCheckInOutsideMargin";
    }
  }

  const hasCheckOut =
    values.checkOutDate?.trim() &&
    values.checkOutTime?.trim() &&
    !errors.checkOutDate &&
    !errors.checkOutTime;

  if (
    hasCheckOut &&
    !isDateTimeInFuture(values.checkOutDate, values.checkOutTime)
  ) {
    if (
      !isCheckOutWithinSlotMargin({
        dateStr: values.checkOutDate,
        timeStr: values.checkOutTime,
        slotType,
        event,
        applicant,
      })
    ) {
      errors.checkOutTime =
        slotType === TIMESHEET_SLOT_TABS.trainings
          ? "slotTrainingCheckOutOutsideMargin"
          : "slotShiftCheckOutOutsideMargin";
    }
  }
};

export const validateAddSlotForm = (values, event, context = {}) => {
  const errors = {};
  const { minDate, maxDate } = getSlotDateBounds(event, context.slotType);

  if (!values.checkInDate?.trim()) {
    errors.checkInDate = "generalFieldRequired";
  } else if (!dayjs(values.checkInDate).isValid()) {
    errors.checkInDate = "invalidDate";
  } else if (isDateOutsideEventBounds(values.checkInDate, minDate, maxDate)) {
    errors.checkInDate = "slotDateOutsideEventPeriod";
  }

  if (!values.checkInTime?.trim()) {
    errors.checkInTime = "generalFieldRequired";
  } else if (!dayjs(values.checkInTime, "HH:mm").isValid()) {
    errors.checkInTime = "invalidTime";
  } else if (isDateTimeInFuture(values.checkInDate, values.checkInTime)) {
    errors.checkInTime = "slotDateTimeCannotBeFuture";
  }

  validateCheckOutFields(values, errors, minDate, maxDate);
  validateSlotMarginFields(values, errors, { ...context, event });

  if (context.originalSlot) {
    validateEditCheckoutPreserved(values, errors, context.originalSlot);
  }

  return errors;
};

export const hasAddSlotErrors = (errors) => Object.keys(errors).length > 0;

export const isAddSlotSaveDisabled = (values, event, context = {}) => {
  if (!values.checkInDate?.trim() || !values.checkInTime?.trim()) {
    return true;
  }

  return hasAddSlotErrors(validateAddSlotForm(values, event, context));
};

export const getEventDateBounds = (event) => {
  const today = dayjs().endOf("day");
  const eventStart = event?.event_start_at
    ? dayjs(event.event_start_at).startOf("day")
    : null;
  const eventEnd = event?.event_end_at
    ? dayjs(event.event_end_at).endOf("day")
    : null;

  let maxDate = today;
  if (eventEnd?.isValid() && eventEnd.isBefore(maxDate)) {
    maxDate = eventEnd;
  }

  return {
    minDate: eventStart?.isValid() ? eventStart : undefined,
    maxDate: maxDate.isValid() ? maxDate : today,
  };
};

export const getSlotDateBounds = (event, slotType) => {
  if (slotType === TIMESHEET_SLOT_TABS.trainings) {
    return getEventTrainingDateBounds(event);
  }

  return getEventDateBounds(event);
};

export const getAddSlotDatePickerProps = (
  event,
  selectedDateValue = "",
  slotType,
) => {
  const { minDate, maxDate } = getSlotDateBounds(event, slotType);

  const resolveReferenceDate = () => {
    if (selectedDateValue?.trim()) {
      const selected = dayjs(selectedDateValue);
      if (selected.isValid()) {
        return selected;
      }
    }

    if (minDate?.isValid()) {
      return minDate;
    }

    if (maxDate?.isValid()) {
      return maxDate;
    }

    return dayjs();
  };

  const referenceDate = resolveReferenceDate();

  const shouldDisableDate = (date) => {
    const day = dayjs(date);
    if (!day.isValid()) {
      return true;
    }

    if (minDate?.isValid() && day.isBefore(minDate, "day")) {
      return true;
    }

    if (maxDate?.isValid() && day.isAfter(maxDate, "day")) {
      return true;
    }

    return false;
  };

  const shouldDisableMonth = (month) => {
    const monthDayjs = dayjs(month);
    if (!monthDayjs.isValid()) {
      return true;
    }

    const monthStart = monthDayjs.startOf("month");
    const monthEnd = monthDayjs.endOf("month");

    if (minDate?.isValid() && monthEnd.isBefore(minDate, "day")) {
      return true;
    }

    if (maxDate?.isValid() && monthStart.isAfter(maxDate, "day")) {
      return true;
    }

    return false;
  };

  return {
    minDate,
    maxDate,
    defaultCalendarMonth: referenceDate,
    calendarKey: referenceDate.format("YYYY-MM"),
    shouldDisableDate,
    shouldDisableMonth,
    preventManualDateEntry: true,
  };
};
